import {useEffect, useMemo, useRef, useState} from 'react'
import {useLayout} from '../../../../_metronic/layout/core'
import {KTCard, KTCardBody, KTIcon} from '../../../../_metronic/helpers'
import {useTable, useExpanded, Row, TableState} from 'react-table'
import toast from 'react-hot-toast'
import * as XLSX from 'xlsx'
import {Loader} from '../../product-master/loader'
import {getReceivableAndPayableSheetColumns} from './Columns'
import {formatNumber} from '../../../../_metronic/helpers'
import {getRegisterValues, transformedRecord} from './api'

import {
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table'
const ReceivablesAndPayables: React.FC<{
  activeTab: string
}> = ({activeTab}) => {
  const {company, financialYear, fyEndDate, fyStartDate} = useLayout()
  const tableRef = useRef<HTMLTableElement>(null)

  const [toDate, setToDate] = useState<string>('')
  const [fromDate, setFromDate] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<transformedRecord[]>([])
  const [isDateUpdate, setIsDateUpdate] = useState(false)
  const [totalDebit, setTotalDebit] = useState<number>(0)
  const [totalCredit, setTotalCredit] = useState<number>(0)
  const [totalOutstanding, setTotalOutstanding] = useState<number>(0)
  const [currency, setCurrency] = useState<string>('')
  const [expanded, setExpanded] = useState<ExpandedState>({
    1: true,
  })
  const [isButtonHovered, setIsButtonHovered] = useState(false)
  // console.log(expanded)
  // Initialize dates from financial year
  useEffect(() => {
    if (fyStartDate && fyEndDate) {
      setFromDate(fyStartDate)
      setToDate(fyEndDate)
    } else {
      const storedFromDate = localStorage.getItem('fromDate')
      const storedToDate = localStorage.getItem('toDate')
      if (!storedFromDate || !storedToDate) {
        const [year1, year2] = financialYear?.value.split('-')
        setFromDate(new Date(Number(year1.trim()), 3, 2).toISOString().split('T')[0])
        setToDate(new Date(Number(year2.trim()), 3, 1).toISOString().split('T')[0])
      } else {
        setFromDate(storedFromDate)
        setToDate(storedToDate)
      }
    }
    setIsDateUpdate(true)
  }, [fyStartDate, fyEndDate])

  useEffect(() => {
    if (isDateUpdate) {
      localStorage.setItem('fromDate', fromDate)
      localStorage.setItem('toDate', toDate)
    }
  }, [isDateUpdate])

  // const columns = useMemo(
  //   () =>
  //     getSalesAndPurchaseColumns(
  //       fromDate,
  //       toDate,
  //       totalDebit,
  //       totalCredit,
  //       totalOutstanding,
  //       currency
  //     ),
  //   [fromDate, toDate, totalDebit, totalCredit, totalOutstanding, currency]
  // )

  const columns = useMemo(
    () => getReceivableAndPayableSheetColumns(fromDate, toDate, totalCredit, totalDebit),
    [fromDate, toDate, totalCredit, totalDebit]
  )
  // Fetch data effect
  useEffect(() => {
    const getData = async () => {
      if (
        company?.value &&
        financialYear?.value &&
        fromDate &&
        toDate &&
        activeTab &&
        isDateUpdate
      ) {
        setIsError(false)
        setIsLoading(true)
        try {
          const res = await getRegisterValues(
            activeTab,
            Number(company?.value),
            financialYear.value,
            fromDate,
            toDate
          )
          if (res) {
            setData(res.records)
            setTotalDebit(res.totalDebit || 0)
            setTotalCredit(res.totalCredit || 0)
            setTotalOutstanding(res.totalSales || 0)
            setCurrency(res.currency || '')
          }
        } catch (e: any) {
          e && toast.error(e.message || 'Something Went Wrong, Please try again later.')
          setData([])
          setTotalDebit(0)
          setTotalCredit(0)
          setTotalOutstanding(0)
          setCurrency('')
          setIsError(true)
        } finally {
          setIsLoading(false)
          setIsDateUpdate(false)
        }
      }
    }
    getData()
  }, [activeTab, company?.value, financialYear?.value, isDateUpdate, fromDate, toDate])

  const registerTable = useReactTable({
    columns,
    data,
    state: {
      expanded,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.subLedger,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => !!row.original.subLedger?.length,
  })

  const TableSection = ({
    title,
    table,
    tableRef,
  }: {
    title: string
    table: any
    tableRef: React.RefObject<HTMLTableElement>
  }) => {
    // // console.log(table.getHeaderGroups())
    return (
      <div className='mb-10'>
        {/* <h3 className='fs-4 fw-bold mb-3'>{title}</h3> */}
        <div className='table-responsive'>
          <table className='table align-middle fs-6 dataTable' ref={tableRef}>
            <thead>
              {table.getHeaderGroups().map((headerGroup: any) => (
                <tr
                  key={headerGroup.id}
                  className='text-center bg-light ps-4 fs-7 text-uppercase gs-0'
                >
                  {headerGroup.headers.map((header: any) => (
                    <th
                      key={header.index}
                      colSpan={header.colSpan}
                      className='min-w-125px'
                      style={{
                        fontSize: '12px',
                        fontWeight: '700',
                        color: '#343541',
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody
              className='text-center'
              style={{
                fontSize: '11px',
                fontWeight: '500',
                color: '#343541',
              }}
            >
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row: any) => (
                  <tr
                    key={row.id}
                    onClick={row.getCanExpand() ? () => row.toggleExpanded() : undefined}
                    className={
                      row.getCanExpand() ? 'cursor-pointer bg-hover-light' : 'bg-hover-light'
                    }
                  >
                    {row.getVisibleCells().map((cell: any) => (
                      <td
                        key={cell.id}
                        style={{
                          paddingLeft: '0.5rem',
                          // paddingLeft: '0.5rem',
                          // border: '1px solid #e1e3ea',
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={table.getAllColumns().length} className='text-center py-4'>
                    No data available to show
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot
              style={{
                fontSize: '11px',
                fontWeight: '500',
                color: '#343541',
              }}
            >
              {table.getFooterGroups().map((footerGroup: any) => (
                <tr key={footerGroup.id} className='bg-hover-light'>
                  {footerGroup.headers.map((header: any) => (
                    <td key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.footer, header.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tfoot>
          </table>
        </div>
        <div className='bg-light d-flex justify-content-between px-3 text-dark fw-bolder py-5'>
          <span
            className='col-2 d-flex justify-content-center text-primary'
            style={{fontSize: '12px', fontWeight: '600'}}
          >
            Total Outstanding
          </span>
          <span style={{fontSize: '14px', fontWeight: '800'}}>
            {/* {currency}  */}
            {formatNumber(totalOutstanding)}
          </span>
        </div>
      </div>
    )
  }
  // Rest of your component remains largely the same, just update the table rendering:
  return (
    <>
      <KTCard className='mb-5'>
        <div className='card-header border-0'>
          <div className='card-title'>
            {/* begin::Search */}
            <div className='d-flex align-items-center position-relative my-1'>
              <input
                type='date'
                data-kt-user-table-filter='search'
                className='form-control form-control-solid w-250px ps-14 me-3'
                placeholder='Search'
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
              <input
                type='date'
                data-kt-user-table-filter='search'
                className='form-control form-control-solid w-250px ps-14'
                placeholder='Search'
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
              <button
                type='button'
                className='btn btn-lg ms-3'
                style={{
                  borderRadius: '1000%',
                  height: '30px',
                  width: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  backgroundColor: !isButtonHovered ? '#f0f0f0' : '#b0b0b0',
                }}
                onMouseEnter={() => setIsButtonHovered(true)}
                onMouseLeave={() => setIsButtonHovered(false)}
                onClick={() => {
                  if (!fromDate || !toDate)
                    toast.error('Please make sure both the From and To dates are selected')
                  else setIsDateUpdate(true)
                }}
              >
                <KTIcon iconName='arrows-circle' className='fs-3 px-0' />
              </button>
            </div>
            {/* end::Search */}
          </div>
          {/* begin::Card toolbar */}
          <div className='card-toolbar'>
            {/* begin::Group actions */}
            {
              <div className='d-flex justify-content-end gap-3' data-kt-user-table-toolbar='base'>
                <button
                  type='button'
                  className='btn p-0'
                  style={{
                    backgroundColor: 'var(--bs-primary)',
                    color: 'var(--bs-primary-inverse)',
                    display: 'inline-flex',
                    height: '43.59px',
                    alignItems: 'center',
                    justifyContent: 'space-between',

                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  }}
                  disabled={!data.length}
                  onClick={() => {
                    toast.success('Download has been started successfully')
                    const ws = XLSX.utils.table_to_sheet(tableRef.current)

                    // Add additional data to the worksheet if needed

                    const wb = XLSX.utils.book_new()
                    XLSX.utils.book_append_sheet(wb, ws, 'ViewTransactions')

                    // Save the file
                    const fileName = `Register${company.label}_${financialYear.value}.xlsx`
                    XLSX.writeFile(wb, fileName)
                  }}
                >
                  <span className='px-5'>Download</span>
                  <span
                    style={{
                      color: '#fff',
                      backgroundColor: '#006060',
                      padding: '0 15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      borderTopRightRadius: 'inherit',
                      borderBottomRightRadius: 'inherit',
                    }}
                  >
                    <KTIcon iconName='arrow-down' className='fs-2 text-white' />
                  </span>
                </button>
                {/* end::Add user */}
              </div>
            }
            {/* end::Group actions */}
          </div>
          {/* end::Card toolbar */}
        </div>
      </KTCard>
      <KTCard>
        <KTCardBody
          className={`py-4 ${fromDate && toDate && isDateUpdate ? 'position-relative' : ''}`}
        >
          {!isLoading && (
            <>
              <TableSection
                title={activeTab.toUpperCase()}
                table={registerTable}
                tableRef={tableRef}
              />
            </>
          )}

          {isLoading && <Loader isError={isError} />}
        </KTCardBody>
      </KTCard>
    </>
  )
}

export default ReceivablesAndPayables
